import React from 'react';

// Only ambient, infinite animations live here. There is intentionally no
// one-shot entrance/reveal so this component is visually identical to the
// static splash in public/index.html (and the native splash), making the
// handoff between all three layers seamless (no flash / re-animation).
const LOADING_SCREEN_STYLES = `
  @keyframes loadingScreenEmberFlicker {
    0%, 100% { opacity: 0.06; }
    35%       { opacity: 0.5; }
    65%       { opacity: 0.34; }
  }
  @keyframes loadingScreenSmokeRise {
    0%   { stroke-dashoffset: 68; opacity: 0; }
    10%  { opacity: 0.5; }
    75%  { stroke-dashoffset: 0; opacity: 0.22; }
    100% { stroke-dashoffset: 0; opacity: 0; }
  }
  @keyframes loadingScreenPipeAmb {
    0%, 100% { opacity: 0; }
    50%       { opacity: 0.28; }
  }
  @keyframes loadingScreenDotPulse {
    0%, 80%, 100% { opacity: 0.2; transform: scaleY(1); }
    40%            { opacity: 0.7; transform: scaleY(1.4); }
  }

  .loading-screen-ember { animation: loadingScreenEmberFlicker 2.6s ease-in-out infinite; }
  .loading-screen-smoke {
    stroke-dasharray: 68;
    stroke-dashoffset: 68;
    animation: loadingScreenSmokeRise 4s ease-in-out infinite;
  }
  .loading-screen-p-amb { animation: loadingScreenPipeAmb 3.2s ease-in-out infinite; }

  .loading-screen-d1 { animation: loadingScreenDotPulse 1.8s ease-in-out 0.0s infinite; }
  .loading-screen-d2 { animation: loadingScreenDotPulse 1.8s ease-in-out 0.3s infinite; }
  .loading-screen-d3 { animation: loadingScreenDotPulse 1.8s ease-in-out 0.6s infinite; }
`;

const LoadingScreen = () => (
  <>
    <style>{LOADING_SCREEN_STYLES}</style>

    <div
      style={{
        width: '100%',
        height: '100%',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#F5F1EA',
      }}
    >
      <div
        style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background:
            'radial-gradient(ellipse 75% 65% at 50% 58%, transparent 42%, rgba(72, 50, 28, 0.08) 100%)',
        }}
      />

      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '52px',
        }}
      >
        <div>
          <svg
            viewBox="0 0 310 195"
            width="310"
            height="195"
            xmlns="http://www.w3.org/2000/svg"
            style={{ display: 'block' }}
            aria-hidden="true"
          >
            <defs>
              <radialGradient id="loadingScreenPipeGlowGrad" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#C68B45" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#C68B45" stopOpacity="0" />
              </radialGradient>
              <radialGradient id="loadingScreenEmberGrad" cx="50%" cy="60%" r="50%">
                <stop offset="0%" stopColor="#E6C486" stopOpacity="1" />
                <stop offset="60%" stopColor="#C68B45" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#A9722E" stopOpacity="0" />
              </radialGradient>
            </defs>

            <ellipse
              cx="214"
              cy="132"
              rx="26"
              ry="18"
              fill="url(#loadingScreenPipeGlowGrad)"
              className="loading-screen-p-amb"
            />

            <rect x="8" y="145" width="294" height="4" rx="0" fill="#C8B898" opacity="0.3" />
            <rect x="8" y="149" width="294" height="14" rx="2" fill="#7A5C3E" />
            <rect x="8" y="149" width="294" height="2.5" rx="1" fill="#9B7A56" opacity="0.55" />
            <rect x="8" y="159" width="294" height="4" rx="1" fill="#4A3222" opacity="0.55" />

            <g>
              <rect x="20" y="53" width="27" height="96" rx="2" fill="#61694A" />
              <rect x="20" y="53" width="3" height="96" fill="rgba(0,0,0,0.18)" rx="1" />
              <rect x="44" y="53" width="3" height="96" fill="rgba(255,255,255,0.08)" rx="1" />
              <rect x="26" y="84" width="15" height="1" rx="0.5" fill="rgba(255,255,255,0.28)" />
              <rect x="26" y="88" width="11" height="1" rx="0.5" fill="rgba(255,255,255,0.18)" />
              <rect x="26" y="104" width="8" height="1" rx="0.5" fill="rgba(255,255,255,0.12)" />
            </g>

            <g>
              <rect x="49" y="71" width="19" height="78" rx="2" fill="#96694A" />
              <rect x="49" y="71" width="2.5" height="78" fill="rgba(0,0,0,0.2)" rx="1" />
              <rect x="64" y="71" width="4" height="78" fill="rgba(255,255,255,0.06)" rx="1" />
              <rect x="53" y="102" width="11" height="1" rx="0.5" fill="rgba(255,255,255,0.22)" />
              <rect x="53" y="106" width="8" height="1" rx="0.5" fill="rgba(255,255,255,0.14)" />
            </g>

            <g>
              <rect x="70" y="56" width="16" height="93" rx="2" fill="#2B2A25" />
              <rect x="70" y="56" width="2.5" height="93" fill="rgba(0,0,0,0.22)" rx="1" />
              <rect x="83" y="56" width="3" height="93" fill="rgba(255,255,255,0.05)" rx="1" />
              <rect x="74" y="89" width="8" height="1" rx="0.5" fill="rgba(255,255,255,0.18)" />
              <rect x="74" y="93" width="6" height="1" rx="0.5" fill="rgba(255,255,255,0.12)" />
              <rect x="74" y="115" width="7" height="1" rx="0.5" fill="rgba(255,255,255,0.1)" />
            </g>

            <g>
              <rect x="88" y="62" width="29" height="87" rx="2" fill="#6B3C36" />
              <rect x="88" y="62" width="3.5" height="87" fill="rgba(0,0,0,0.18)" rx="1" />
              <rect x="113" y="62" width="4" height="87" fill="rgba(255,255,255,0.06)" rx="1" />
              <rect x="94" y="94" width="17" height="1" rx="0.5" fill="rgba(255,255,255,0.22)" />
              <rect x="94" y="98" width="13" height="1" rx="0.5" fill="rgba(255,255,255,0.15)" />
              <rect x="94" y="115" width="9" height="1" rx="0.5" fill="rgba(255,255,255,0.1)" />
            </g>

            <g transform="rotate(-6, 132, 149)">
              <rect x="120" y="69" width="24" height="80" rx="2" fill="#A88C52" />
              <rect x="120" y="69" width="3" height="80" fill="rgba(0,0,0,0.16)" rx="1" />
              <rect x="141" y="69" width="3" height="80" fill="rgba(255,255,255,0.07)" rx="1" />
              <rect x="126" y="100" width="13" height="1" rx="0.5" fill="rgba(255,255,255,0.25)" />
              <rect x="126" y="104" width="9" height="1" rx="0.5" fill="rgba(255,255,255,0.16)" />
            </g>

            <rect x="156" y="143" width="50" height="6" rx="3" fill="#2E1E10" />
            <rect x="158" y="143" width="48" height="2" rx="1" fill="#5C3F27" opacity="0.55" />

            <path
              d="M 197 149 L 197 127 Q 197 119 205 119 L 213 119 Q 221 119 221 127 L 221 149 Z"
              fill="#2E1E10"
            />
            <path
              d="M 219 127 Q 219 122 215 120 L 218 120 Q 221 123 221 127 L 221 149 L 219 149 Z"
              fill="#5C3F27"
              opacity="0.5"
            />
            <path
              d="M 197 149 L 197 127 Q 197 122 200 120 L 198 120 Q 196 122 196 127 L 196 149 Z"
              fill="rgba(0,0,0,0.3)"
            />

            <ellipse cx="209" cy="119.5" rx="8" ry="3.2" fill="#130D06" />

            <ellipse
              cx="209"
              cy="124"
              rx="4.5"
              ry="2"
              fill="url(#loadingScreenEmberGrad)"
              className="loading-screen-ember"
            />

            <path
              d="M 209 118 C 205 109, 214 102, 208 93 C 202 84, 212 77, 206 68"
              stroke="#C8B8A0"
              strokeWidth="1.5"
              fill="none"
              strokeLinecap="round"
              className="loading-screen-smoke"
            />
          </svg>
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            userSelect: 'none',
          }}
        >
          <div
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: '30px',
              fontWeight: 400,
              color: '#3D2E1E',
              letterSpacing: '0.16em',
              lineHeight: 1,
            }}
          >
            CBC
          </div>

          <div
            style={{
              width: '32px',
              height: '1px',
              backgroundColor: '#C4B89A',
              margin: '12px auto',
              opacity: 0.7,
            }}
          />

          <div
            style={{
              fontFamily: 'Georgia, serif',
              fontSize: '11px',
              fontWeight: 300,
              color: '#8B7355',
              letterSpacing: '0.32em',
              textTransform: 'uppercase',
            }}
          >
            Conscious Book Club
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'center',
              gap: '5px',
              marginTop: '32px',
            }}
          >
            {[
              { cls: 'loading-screen-d1', h: 10 },
              { cls: 'loading-screen-d2', h: 14 },
              { cls: 'loading-screen-d3', h: 10 },
            ].map(({ cls, h }) => (
              <div
                key={cls}
                className={cls}
                style={{
                  width: '2px',
                  height: `${h}px`,
                  borderRadius: '1px',
                  backgroundColor: '#C4B89A',
                  transformOrigin: 'bottom center',
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  </>
);

export default LoadingScreen;
